import { useEffect, useMemo, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import PhoneMock from "@/components/PhoneMock";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Printer,
  Package,
  Truck,
  CalendarCheck,
  CheckCircle2,
  Send,
  AlertTriangle,
  DollarSign,
  User,
  Wrench,
  TrendingUp,
  Users,
} from "lucide-react";
import RevenueSparkline from "@/components/RevenueSparkline";

function money(n) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n || 0);
}

// Aging color for shelf inventory. Dealer HATES seeing red on his iPad.
function ageStyle(days) {
  if (days == null) return "";
  if (days <= 3) return "bg-emerald-50 border-l-4 border-l-emerald-500 dark:bg-emerald-950/20";
  if (days <= 7) return "bg-yellow-50 border-l-4 border-l-yellow-500 dark:bg-yellow-950/20";
  return "bg-red-50 border-l-4 border-l-red-500 dark:bg-red-950/20";
}
function ageDot(days) {
  if (days == null) return "bg-neutral-400";
  if (days <= 3) return "bg-emerald-500";
  if (days <= 7) return "bg-yellow-500";
  return "bg-red-500";
}

const COLUMNS = [
  { key: "ordered", title: "Ordered", sub: "Waiting on part", Icon: Package, tint: "bg-[hsl(var(--gold))] text-neutral-900" },
  { key: "arrived", title: "Arrived", sub: "Fire the text!", Icon: Truck, tint: "bg-red-600 text-white" },
  { key: "appt_set", title: "Appointment Set", sub: "Confirmed install date", Icon: CalendarCheck, tint: "bg-blue-600 text-white" },
  { key: "installed", title: "Installed", sub: "RO closed · $ recovered", Icon: CheckCircle2, tint: "bg-emerald-600 text-white" },
];

export default function PartsPipelinePage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [firedIds, setFiredIds] = useState(new Set());
  const [advisorFilter, setAdvisorFilter] = useState("all");

  const load = () => {
    setBusy(true);
    return api
      .get("/parts/pipeline")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatError(e.response?.data?.detail)))
      .finally(() => setBusy(false));
  };
  useEffect(() => {
    load();
  }, []);

  const advisors = useMemo(() => {
    if (!data) return [];
    const names = new Set();
    data.parts.forEach((p) => p.advisor_name && names.add(p.advisor_name));
    return Array.from(names).sort();
  }, [data]);

  const columns = useMemo(() => {
    if (!data) return {};
    const g = { ordered: [], arrived: [], appt_set: [], installed: [] };
    for (const p of data.parts) {
      if (advisorFilter !== "all" && p.advisor_name !== advisorFilter) continue;
      if (g[p.status]) g[p.status].push(p);
    }
    return g;
  }, [data, advisorFilter]);

  const fireText = async (partId) => {
    try {
      await api.post(`/parts/${partId}/fire-arrived-text`);
      const next = new Set(firedIds);
      next.add(partId);
      setFiredIds(next);
      toast.success("Parts-arrived text queued to customer");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const totals = data?.totals || {};
  const arrivedCount = totals.arrived || 0;
  const installedCount = totals.installed || 0;
  // Install rate = installed / (installed + arrived + appt_set) — "of parts on hand, how many made it out the door"
  const denom = installedCount + arrivedCount + (totals.appt_set || 0);
  const installRate = denom > 0 ? Math.round((installedCount / denom) * 100) : 0;

  const dealer = data?.dealership_name || "Your Dealership";
  const sampleArrived = columns.arrived?.[0];
  const previewBody = sampleArrived
    ? `Hi ${sampleArrived.customer_name.split(" ")[0]}, your special-order ${sampleArrived.part_name} for your ${sampleArrived.vehicle} just arrived at ${dealer}. Tap below to schedule install — or speak live with your advisor.`
    : `Hi {first_name}, your special-order {part_name} for your {vehicle} just arrived at ${dealer}. Tap below to schedule install — or speak live with your advisor.`;

  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="Parts Pipeline" />
      <div className="mb-6 flex items-start justify-between gap-4 no-print">
        <div>
          <div className="font-mono-label text-muted-foreground">Special Order Tracking</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Parts Pipeline</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-3xl leading-relaxed">
            Every special-order part from <b>ordered → arrived → appointment set → installed</b>.
            One tap on any Arrived card fires the &quot;your parts are in&quot; text with a schedule
            link and a live-call button. <b>Nobody else in the industry closes this loop.</b>
          </p>
        </div>
        <Button variant="outline" onClick={() => window.print()} data-testid="print-parts-btn">
          <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Print
        </Button>
      </div>

      {/* Simple KPI dashboard — parts not arrived, installed, % */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Card className="p-4 border-border !bg-[hsl(var(--gold))]/10 border-[hsl(var(--gold))]/40" data-testid="parts-kpi-not-arrived">
          <div className="font-mono-label text-neutral-900 text-[0.6rem]">Not Arrived</div>
          <div className="font-display font-black text-3xl tracking-tighter mt-1 tabular-nums">
            {totals.ordered ?? "—"}
          </div>
          <div className="text-[0.65rem] text-neutral-700 mt-0.5">on order</div>
        </Card>
        <Card className="p-4 border-border !bg-red-50 dark:!bg-red-950/20" data-testid="parts-kpi-arrived">
          <div className="font-mono-label text-red-700 dark:text-red-300 text-[0.6rem] flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" strokeWidth={2} />
            Awaiting Text
          </div>
          <div className="font-display font-black text-3xl tracking-tighter mt-1 tabular-nums text-red-700 dark:text-red-300">
            {arrivedCount}
          </div>
          <div className="text-[0.65rem] text-red-700/70 dark:text-red-300/70 mt-0.5">
            fire these NOW
          </div>
        </Card>
        <Card className="p-4 border-border" data-testid="parts-kpi-appt">
          <div className="font-mono-label text-muted-foreground text-[0.6rem]">Appt. Set</div>
          <div className="font-display font-black text-3xl tracking-tighter mt-1 tabular-nums text-blue-600">
            {totals.appt_set ?? 0}
          </div>
          <div className="text-[0.65rem] text-muted-foreground mt-0.5">confirmed install</div>
        </Card>
        <Card className="p-4 border-border" data-testid="parts-kpi-installed">
          <div className="font-mono-label text-muted-foreground text-[0.6rem]">Installed</div>
          <div className="font-display font-black text-3xl tracking-tighter mt-1 tabular-nums text-emerald-600">
            {installedCount}
          </div>
          <div className="text-[0.65rem] text-muted-foreground mt-0.5">this month</div>
        </Card>
        <Card
          className="p-4 border-border !bg-[hsl(var(--gold))]/20 border-[hsl(var(--gold))]/50"
          data-testid="parts-kpi-rate"
        >
          <div className="font-mono-label text-[0.6rem] text-neutral-900">Install Rate</div>
          <div className="font-display font-black text-3xl tracking-tighter mt-1 tabular-nums">
            {installRate}%
          </div>
          <div className="text-[0.65rem] text-neutral-700 mt-0.5">
            installed ÷ on-hand
          </div>
        </Card>
      </div>

      {/* Value comparison strip — parts on shelf vs installed vs unbilled labor.
          The dealer's #1 pitch reaction to this row: "I need this today." */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="parts-value-strip">
        <Card
          className="p-4 border-l-4 border-l-red-500 bg-red-50/60 dark:bg-red-950/20"
          data-testid="parts-val-shelf"
        >
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-md bg-red-500 grid place-items-center shrink-0">
              <Package className="w-4 h-4 text-white" strokeWidth={2.2} />
            </div>
            <div className="font-mono-label text-red-800 dark:text-red-300 text-[0.6rem] tracking-widest">
              Parts $ on Shelf
            </div>
          </div>
          <div className="font-display font-black text-3xl tabular-nums tracking-tighter text-red-800 dark:text-red-200">
            {money(totals.shelf_value)}
          </div>
          <div className="text-[0.65rem] text-red-700/80 dark:text-red-300/70 mt-1 leading-snug">
            Ordered + Arrived + Appt-Set. <b>Cash sitting still</b> — not yet paying you back.
          </div>
        </Card>

        <Card
          className="p-4 border-l-4 border-l-yellow-500 bg-yellow-50/60 dark:bg-yellow-950/20"
          data-testid="parts-val-unbilled"
        >
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-md bg-yellow-500 grid place-items-center shrink-0">
              <Wrench className="w-4 h-4 text-neutral-900" strokeWidth={2.2} />
            </div>
            <div className="font-mono-label text-yellow-900 dark:text-yellow-300 text-[0.6rem] tracking-widest">
              Labor $ Not Billed Yet
            </div>
          </div>
          <div className="font-display font-black text-3xl tabular-nums tracking-tighter text-yellow-900 dark:text-yellow-200">
            {money(totals.unbilled_labor)}
          </div>
          <div className="text-[0.65rem] text-yellow-800/80 dark:text-yellow-300/70 mt-1 leading-snug">
            Committed work on Arrived + Appt-Set parts. Every day this waits,
            your bays sit half-earning.
          </div>
        </Card>

        <Card
          className="p-4 border-l-4 border-l-emerald-500 bg-emerald-50/60 dark:bg-emerald-950/20"
          data-testid="parts-val-installed"
        >
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-md bg-emerald-600 grid place-items-center shrink-0">
              <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={2.2} />
            </div>
            <div className="font-mono-label text-emerald-800 dark:text-emerald-300 text-[0.6rem] tracking-widest">
              Installed · Collected
            </div>
          </div>
          <div className="font-display font-black text-3xl tabular-nums tracking-tighter text-emerald-800 dark:text-emerald-200">
            {money(totals.collected_total)}
          </div>
          <div className="text-[0.65rem] text-emerald-700/80 dark:text-emerald-300/70 mt-1 leading-snug">
            Parts <b>{money(totals.installed_parts_value)}</b> + Labor{" "}
            <b>{money(totals.billed_labor)}</b>. What&apos;s actually paid you back.
          </div>
        </Card>
      </div>

      {/* Compact aging alert — kept because dealers respond to the "oldest part" shame number */}
      {totals.oldest_arrived_days > 0 && (
        <Card
          className="p-3 mb-6 border-l-4 border-l-[hsl(var(--gold))] bg-[hsl(var(--gold))]/[0.05] flex items-center gap-3"
          data-testid="parts-shelf-strip"
        >
          <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" strokeWidth={2} />
          <div className="flex-1 text-sm">
            Oldest arrived part has been on the shelf{" "}
            <b className="text-red-600 tabular-nums">{totals.oldest_arrived_days} days</b>. Fire
            that one first — it&apos;s the &quot;shame column&quot; in the Arrived kanban.
          </div>
          <div className="font-display font-bold text-sm text-neutral-900">
            {money(totals.at_risk_total)} <span className="text-xs font-normal text-muted-foreground">at risk</span>
          </div>
        </Card>
      )}

      {/* 30-day installed revenue trend — the hockey-stick that closes dealer principals */}
      {data?.trend && (
        <Card
          className="p-5 mb-6 border-emerald-200 dark:border-emerald-900 bg-gradient-to-br from-emerald-50 to-emerald-50/40 dark:from-emerald-950/30 dark:to-emerald-950/10"
          data-testid="parts-trend-card"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-5">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-md bg-emerald-600 grid place-items-center shrink-0">
                  <TrendingUp className="w-4 h-4 text-white" strokeWidth={2.2} />
                </div>
                <div className="font-mono-label text-emerald-800 dark:text-emerald-300 text-[0.6rem] tracking-widest">
                  Installed Revenue · Last 30 Days
                </div>
              </div>
              <div className="flex items-baseline gap-3 mt-1">
                <div className="font-display font-black text-4xl tabular-nums tracking-tighter text-emerald-800 dark:text-emerald-100">
                  {money(data.trend.current_30d_value)}
                </div>
                <div
                  className={`font-display font-bold text-sm inline-flex items-center gap-1 tabular-nums ${
                    data.trend.delta_pct >= 0
                      ? "text-emerald-700 dark:text-emerald-300"
                      : "text-red-600"
                  }`}
                  data-testid="parts-trend-delta"
                >
                  <TrendingUp
                    className={`w-3.5 h-3.5 ${data.trend.delta_pct < 0 ? "rotate-180" : ""}`}
                    strokeWidth={2.5}
                  />
                  {data.trend.delta_pct >= 0 ? "+" : ""}
                  {data.trend.delta_pct}%
                </div>
              </div>
              <div className="text-[0.65rem] text-emerald-700/80 dark:text-emerald-300/70 mt-1 leading-snug">
                Parts + labor closed on installed ROs. Compared to prior 30 days ({money(data.trend.prior_30d_value)}).
              </div>
            </div>
            <div className="w-full md:w-[280px] md:pl-4 md:border-l md:border-emerald-200 dark:md:border-emerald-800">
              <RevenueSparkline data={data.trend.days} color="#059669" />
              <div className="flex justify-between mt-1 text-[0.55rem] text-emerald-700/70 dark:text-emerald-300/60 tabular-nums font-mono-label">
                <span>30d ago</span>
                <span>today</span>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Advisor filter chips — service manager huddle tool */}
      {advisors.length > 0 && (
        <div className="flex items-center gap-2 mb-4 no-print flex-wrap" data-testid="parts-advisor-filter">
          <div className="font-mono-label text-[0.6rem] text-muted-foreground inline-flex items-center gap-1 tracking-widest">
            <Users className="w-3 h-3" strokeWidth={2} />
            Filter by advisor:
          </div>
          <button
            onClick={() => setAdvisorFilter("all")}
            data-testid="parts-advisor-all"
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
              advisorFilter === "all"
                ? "bg-neutral-900 text-white border-neutral-900"
                : "border-border hover:bg-muted"
            }`}
          >
            All ({data?.parts?.length || 0})
          </button>
          {advisors.map((name) => {
            const count = data?.parts?.filter((p) => p.advisor_name === name).length || 0;
            const active = advisorFilter === name;
            return (
              <button
                key={name}
                onClick={() => setAdvisorFilter(name)}
                data-testid={`parts-advisor-${name.replace(/\s+/g, "-")}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                  active
                    ? "bg-neutral-900 text-white border-neutral-900"
                    : "border-border hover:bg-muted"
                }`}
              >
                {name} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6" data-testid="parts-kanban">
        {COLUMNS.map((col) => {
          const list = columns[col.key] || [];
          return (
            <div key={col.key} className="min-w-0" data-testid={`parts-col-${col.key}`}>
              <div
                className={`px-3 py-2 rounded-t-md flex items-center gap-2 ${col.tint}`}
              >
                <col.Icon className="w-4 h-4" strokeWidth={2.2} />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm truncate">{col.title}</div>
                  <div className="text-[0.55rem] opacity-85 truncate">{col.sub}</div>
                </div>
                <div className="font-display font-black text-lg tabular-nums">{list.length}</div>
              </div>
              <div className="border border-t-0 border-border rounded-b-md p-2 bg-muted/20 space-y-2 min-h-[240px]">
                {list.length === 0 && (
                  <div className="text-xs text-muted-foreground text-center py-8">
                    Nothing in this stage right now.
                  </div>
                )}
                {list.map((p) => {
                  const isArrived = col.key === "arrived";
                  const days = p.days_on_shelf;
                  const fired = firedIds.has(p.id);
                  return (
                    <div
                      key={p.id}
                      className={`rounded-md p-3 border border-border bg-background shadow-sm ${
                        isArrived ? ageStyle(days) : ""
                      }`}
                      data-testid={`parts-card-${p.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-display font-bold text-sm truncate">
                            {p.customer_name}
                          </div>
                          <div className="text-[0.65rem] text-muted-foreground truncate">
                            {p.vehicle}
                          </div>
                        </div>
                        {isArrived && (
                          <div className="flex flex-col items-end shrink-0">
                            <span
                              className={`w-2 h-2 rounded-full ${ageDot(days)}`}
                              aria-hidden
                            />
                            <div className="font-mono-label text-[0.5rem] text-muted-foreground mt-0.5 tabular-nums">
                              {days}d
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="mt-2 text-xs font-medium leading-snug">{p.part_name}</div>
                      {p.last_ro_number && (
                        <div className="mt-0.5 text-[0.6rem] text-muted-foreground tabular-nums">
                          from{" "}
                          <span className="font-mono font-semibold text-foreground/80">
                            {p.last_ro_number}
                          </span>
                          {p.last_ro_on ? (
                            <>
                              {" "}·{" "}
                              {new Date(p.last_ro_on).toLocaleDateString([], {
                                month: "short",
                                day: "numeric",
                              })}
                            </>
                          ) : null}
                        </div>
                      )}
                      <div className="mt-1 flex items-center justify-between text-[0.6rem] text-muted-foreground tabular-nums">
                        <span>Parts {money(p.part_cost)}</span>
                        <span>Labor {money(p.labor_cost)}</span>
                      </div>
                      <div className="mt-1.5 text-[0.6rem] text-muted-foreground inline-flex items-center gap-1">
                        <User className="w-2.5 h-2.5" strokeWidth={2} />
                        {p.advisor_name}
                      </div>

                      {isArrived && (
                        <Button
                          size="sm"
                          onClick={() => fireText(p.id)}
                          disabled={fired}
                          className={`w-full mt-2 h-7 text-[0.65rem] ${
                            fired
                              ? "bg-emerald-600 hover:bg-emerald-600 text-white"
                              : "bg-red-600 hover:bg-red-700 text-white"
                          }`}
                          data-testid={`parts-fire-${p.id}`}
                        >
                          {fired ? (
                            <>
                              <CheckCircle2 className="w-3 h-3 mr-1" strokeWidth={2.5} />
                              Sent
                            </>
                          ) : (
                            <>
                              <Send className="w-3 h-3 mr-1" strokeWidth={2.5} />
                              Fire Arrived Text
                            </>
                          )}
                        </Button>
                      )}
                      {col.key === "appt_set" && p.appointment_on && (
                        <div className="mt-2 text-[0.6rem] text-blue-700 dark:text-blue-300 font-semibold tabular-nums">
                          Install: {new Date(p.appointment_on).toLocaleDateString([], { month: "short", day: "numeric" })}
                        </div>
                      )}
                      {col.key === "installed" && p.installed_on && (
                        <div className="mt-2 text-[0.6rem] text-emerald-700 dark:text-emerald-300 font-semibold tabular-nums">
                          Installed {new Date(p.installed_on).toLocaleDateString([], { month: "short", day: "numeric" })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Example text preview + legend */}
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 mb-6">
        <div>
          <Card className="p-5 border-border">
            <div className="font-mono-label text-muted-foreground text-[0.6rem] mb-1">
              The Text Every Arrived Card Fires
            </div>
            <div className="font-display font-bold text-lg tracking-tight mb-3">
              &quot;Your special-order parts arrived&quot;
            </div>
            <div className="text-sm text-muted-foreground leading-relaxed">
              Fires the second an Arrived card is tapped. Personalized with the
              customer&apos;s first name, their exact vehicle, and the specific part
              they were waiting on. Two tap-buttons follow: <b>Schedule Install</b>
              &nbsp;or&nbsp;<b>Speak Live</b> to a service advisor.
            </div>

            <div className="mt-5 grid grid-cols-3 gap-3">
              <div className="p-3 rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20">
                <div className="font-mono-label text-[0.55rem] text-emerald-700 dark:text-emerald-300 tracking-widest">
                  0 – 3 days
                </div>
                <div className="font-display font-black text-sm text-emerald-800 dark:text-emerald-200 mt-1">
                  Green — Fresh
                </div>
              </div>
              <div className="p-3 rounded-md border border-yellow-300 bg-yellow-50 dark:bg-yellow-950/20">
                <div className="font-mono-label text-[0.55rem] text-yellow-800 tracking-widest">
                  4 – 7 days
                </div>
                <div className="font-display font-black text-sm text-yellow-800 dark:text-yellow-200 mt-1">
                  Yellow — Nudge
                </div>
              </div>
              <div className="p-3 rounded-md border border-red-300 bg-red-50 dark:bg-red-950/20">
                <div className="font-mono-label text-[0.55rem] text-red-700 dark:text-red-300 tracking-widest">
                  8+ days
                </div>
                <div className="font-display font-black text-sm text-red-800 dark:text-red-200 mt-1">
                  Red — Shame
                </div>
              </div>
            </div>
          </Card>
        </div>
        <div className="flex justify-center">
          <PhoneMock
            dealer={dealer}
            from="+1 (913) 555-0100 · Parts Dept."
            body={previewBody}
            sent="Now"
            label="Parts Arrived · Special Order"
            accent="bg-red-600 text-white"
            scheduleUrl="https://drvlgc.co/s/parts"
            callUrl="https://drvlgc.co/c/parts"
            testid="parts-phone-mock"
          />
        </div>
      </div>
    </DashboardShell>
  );
}
